import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, MenuItem, Select, TextField, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'
import { useDebouncedCallback } from 'use-debounce'

import {
    useGetOrderItemsQuery,
    useUpdateOrderItemDrawingMutation,
    useUpdateOrderItemMutation,
} from '@/app/services/orderItems'
import { selectOrganisationId } from '@/app/slices/organisationSlice'
import TbxTooltip from '@/common/components/TbxTooltip/TbxTooltip'

const PartDetails = ({ drawing, orderItemId }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)

    const { orderItem } = useGetOrderItemsQuery(
        {
            organisationId,
            orderId,
        },
        {
            selectFromResult: ({ data }) => ({
                orderItem: data?.find((item) => item.id === orderItemId),
            }),
        }
    )

    const [updateOrderItem] = useUpdateOrderItemMutation()
    const [updateOrderItemDrawing] = useUpdateOrderItemDrawingMutation({ fixedCacheKey: 'updateOrderItemDrawing' })

    const isPartFromLibrary = Boolean(orderItem?.partLibraryEntryId)

    const [itemUnit, setItemUnit] = useState(orderItem?.isImperial ? 'in' : 'mm')
    const [itemName, setItemName] = useState(orderItem?.name || '')
    const [itemNotes, setItemNotes] = useState(orderItem?.vendorNotes || '')

    const handleUpdateOrderItem = async (orderItem) => {
        try {
            await updateOrderItem({
                organisationId,
                orderId,
                orderItemId: orderItem?.id,
                orderItem,
            }).unwrap()
        } catch (error) {
            enqueueSnackbar(t('Failed to update the order item'), {
                variant: 'error',
            })
        }
    }

    const debounceHandleUpdateOrderItem = useDebouncedCallback(handleUpdateOrderItem, 1000)

    const handleNameChange = async (event) => {
        const value = event?.target?.value
        setItemName(value)
        if (Boolean(value) && itemName !== value) {
            await debounceHandleUpdateOrderItem({ ...orderItem, name: value })
        }
    }

    const handleNotesChange = async (event) => {
        const value = event?.target?.value
        setItemNotes(value)
        if (Boolean(value) && itemNotes !== value) {
            await debounceHandleUpdateOrderItem({ ...orderItem, vendorNotes: value })
        }
    }

    const handleUnitChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemUnit !== value) {
            setItemUnit(value)
            const isImperial = value === 'in'
            await handleUpdateOrderItem({ ...orderItem, isImperial: isImperial })

            drawing.paths = Object.values(drawing.paths)
            const updatedDrawing = { ...drawing, isImperial: isImperial }

            await updateOrderItemDrawing({
                organisationId,
                orderId,
                orderItemId,
                drawing: updatedDrawing,
            })
        }
    }

    useEffect(() => {
        setItemUnit(orderItem?.isImperial ? 'in' : 'mm')
        setItemName(orderItem?.name || '')
        setItemNotes(orderItem?.vendorNotes || '')
    }, [orderItem])

    return (
        <Box>
            {isPartFromLibrary ? (
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 1,
                        justifyContent: 'space-between',
                        margin: 1,
                    }}
                >
                    <Box
                        sx={{
                            flex: 1,
                        }}
                    >
                        <Typography>{t('Part ID')}</Typography>
                    </Box>
                    <Box
                        sx={{
                            flex: 2,
                        }}
                    >
                        <Typography>{orderItem?.partId}</Typography>
                    </Box>
                </Box>
            ) : null}
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography
                        color="text.secondary"
                        variant="small"
                    >
                        {t('Part Name')}
                    </Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 2,
                        }}
                    >
                        <TextField
                            disabled={isPartFromLibrary}
                            size="small"
                            slotProps={{
                                htmlInput: {
                                    'data-testid': 'drawingdoctordrawer-partdetails-partname-input',
                                    style: { paddingBlock: '6px', paddingInline: '12px' },
                                },
                                input: {
                                    style: { fontSize: '14px' },
                                },
                            }}
                            value={itemName}
                            variant="outlined"
                            fullWidth
                            onBlur={handleNameChange}
                            onChange={handleNameChange}
                        />
                    </Box>
                </TbxTooltip>
            </Box>
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography
                        color="text.secondary"
                        variant="small"
                    >
                        {t('Units')}
                    </Typography>
                </Box>
                <TbxTooltip
                    title={isPartFromLibrary ? t('Cannot edit parts from library') : ''}
                    arrow
                >
                    <Box
                        sx={{
                            flex: 2,
                        }}
                    >
                        <Select
                            color="primary"
                            disabled={isPartFromLibrary}
                            id="select-itemUnit"
                            inputProps={{
                                'data-testid': 'drawingdoctordrawer-partdetails-units-input',
                            }}
                            labelId="select-itemUnit-label"
                            size="small"
                            sx={{
                                '& .MuiSelect-select': { fontSize: '14px', paddingBlock: '5px', paddingInline: '12px' },
                            }}
                            value={itemUnit}
                            variant="outlined"
                            fullWidth
                            onBlur={handleUnitChange}
                            onChange={handleUnitChange}
                        >
                            <MenuItem value="mm">{t('Millimeters')}</MenuItem>
                            <MenuItem value="in">{t('Inches')}</MenuItem>
                        </Select>
                    </Box>
                </TbxTooltip>
            </Box>
            <Box
                sx={{
                    alignItems: 'top',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    justifyContent: 'space-between',
                    margin: 1,
                }}
            >
                <Box
                    sx={{
                        flex: 1,
                    }}
                >
                    <Typography
                        color="text.secondary"
                        variant="small"
                    >
                        {t('Notes')}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        flex: 2,
                    }}
                >
                    <TextField
                        color="primary"
                        maxRows={4}
                        minRows={4}
                        slotProps={{
                            htmlInput: {
                                'data-testid': 'drawingdoctordrawer-partdetails-notes-input',
                            },
                            input: {
                                style: { fontSize: '14px' },
                            },
                        }}
                        value={itemNotes}
                        variant="outlined"
                        fullWidth
                        multiline
                        onBlur={handleNotesChange}
                        onChange={handleNotesChange}
                    />
                </Box>
            </Box>
        </Box>
    )
}

PartDetails.propTypes = {
    drawing: PropTypes.object,
    orderItemId: PropTypes.string,
}

export default PartDetails
